import React from "react";
import SectionHeader from "@/components/SectionHeader";
import WorkCard from "@/components/WorkCard";

const STORIES = [
  {
    index: "01",
    tag: "The German Frugal Billionaire",
    title: "The millionaire who lives like he's broke.",
    caption: "Meet the world's most frugal billionaire; worth millions, still eats from trash bins. Wealth isn't built on the income statement. It's built on the expense statement.",
    image: "01 Storytelling - A modest lifestyle is the best way to be financially independent.jpg",
    link: "https://www.instagram.com/p/C4YBwsJhYb5/",
  },
  {
    index: "02",
    tag: "FIRE Movement",
    title: "The Google engineer who retired early, and regretted it.",
    caption: "She optimised every expense, maxed every investment, and quit Google expecting bliss. Instead, she didn't know what to do next. FIRE is a numbers game; life isn't.",
    image: "02 Storytelling How FIRE can backfire.jpg",
    link: "https://www.instagram.com/p/C5I3LtwpeLc/",
  },
  {
    index: "03",
    tag: "Two Fund Managers, One Philosophy",
    title: "Rajeev Thakkar vs Sankaran Naren.",
    caption: "Same philosophy. Vastly different execution. Understanding the \u201cwhy\u201d behind a strategy matters more than blindly following it.",
    image: "03 Storytelling - Using Fund Managers Instead of Funds.jpg",
    link: "https://www.instagram.com/p/DV3aGkbkmMR/",
  },
];

export default function StorytellingPage() {
  return (
    <>
      <SectionHeader
        eyebrow="Case Study 05"
        pageIndex="05"
        title="Simplifying Finance Through Storytelling"
        kicker="Stories activate 7 areas of the brain. Data activates 2."
        lede="Finance can be technical: charts, percentages, jargon. Most people don't relate to asset allocation or rebalancing, but they DO relate to other people's lives. We weaponised storytelling, using real human narratives as vehicles for financial truths."
      />

      {/* Why storytelling */}
      <section className="mx-auto max-w-[1440px] px-6 pt-20 lg:px-12">
        <div className="grid gap-px overflow-hidden border border-[#EDE4D3]/12 bg-[#EDE4D3]/10 md:grid-cols-3">
          {[
            ["7×", "Brain regions activated by stories vs charts."],
            ["High", "Engagement, share rates, and recall."],
            ["Behavioural", "Stories don't just inform, they change action."],
          ].map(([v, d]) => (
            <div key={v} className="bg-[#0B0E14] p-8">
              <p className="font-serif text-5xl text-[#D4A574]">{v}</p>
              <p className="mt-4 text-sm text-[#EDE4D3]/70">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stories */}
      <section className="mx-auto max-w-[1440px] px-6 pt-28 lg:px-12">
        <h2 className="mb-12 font-serif text-3xl tracking-tight lg:text-4xl">
          Flagship <span className="italic text-[#D4A574]">stories</span>.
        </h2>

        <div className="space-y-24">
          {STORIES.map((s, i) => (
            <a
              key={s.index}
              href={s.link}
              target="_blank"
              rel="noreferrer"
              data-testid={`story-${s.index}`}
              className={`group grid items-center gap-12 lg:grid-cols-[1fr,1.1fr] ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <StoryImage filename={s.image} />
              <div>
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-[#D4A574]">
                  <span>{s.index}</span>
                  <span className="text-[#EDE4D3]/30">/</span>
                  <span className="text-[#EDE4D3]/55">{s.tag}</span>
                </div>
                <h3 className="mt-4 font-serif text-3xl leading-tight transition-colors group-hover:text-[#D4A574] lg:text-4xl">
                  {s.title}
                </h3>
                <p className="mt-6 max-w-[58ch] text-[15px] leading-relaxed text-[#EDE4D3]/70">
                  {s.caption}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 font-serif text-[15px] italic text-[#EDE4D3] transition-colors group-hover:text-[#D4A574]">
                  See the post
                  <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                </span>
              </div>
            </a>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-32 max-w-[1000px] px-6 text-center lg:px-12">
        <p className="font-serif text-2xl italic leading-snug text-[#EDE4D3]/85 md:text-3xl lg:text-4xl">
          When people see themselves in a <span className="text-[#D4A574]">story</span>, they&rsquo;re more likely to act.
        </p>
      </section>
    </>
  );
}

function StoryImage({ filename }) {
  const ImageFrame = require("@/components/ImageFrame").default;
  return (
    <div className="overflow-hidden border border-[#EDE4D3]/10 card-lift">
      <ImageFrame filename={filename} ratio="1/1" />
    </div>
  );
}
